import React, { useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { ButtonGroup } from '@mui/material'
import { generatePath, useNavigate } from 'react-router-dom'
import { useVenti } from 'venti'
import { SiteConfigurationByUrl } from '@matech/thebigpos-sdk'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { Form } from '../../../../components/formControls'
import useForm from '../../../../hooks/useForm'
import {
	loanCalculatorFormDefaultValues,
	loanCalculatorFormSchema,
	LoanCalculatorFormValues,
} from './LoanCalculatorForm'
import LoanCalculatorSharedFields from '../components/LoanCalculatorSharedFields'
import LoanCalculatorResults from '../components/LoanCalculatorResults'
import { Button, LoadingBtn } from '../../../../components/Button'
import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import { eventTypes } from '../../../../services/constants'
import { navigationLinking } from '../../../../services/navigation'
import { useAppContext } from '../../../../components/AppContext'
import { useMixpanel } from '../../../../hooks/useMixpanel'
import { getTheme } from '../../../../config'
import { LayoutRow } from '../../../../components/layout'
import { formStateErrorsToErrorList } from '../utils/helpers'
import { ModalPleaseWait } from '../../../../components/modals/ModalPleaseWait'
import { useAlert } from '../../../../hooks'
import queryKeys from '../../../../services/queryKeys'

export type LoanComparisonScenarioFormProps = {
	loanId: string
	loading?: boolean
	defaultValues: LoanCalculatorFormValues
	loanLocked?: boolean
	onSubmit?: (data: LoanCalculatorFormValues) => void
	onCopyLoanToScenario?: ({
		index,
		data,
	}: {
		index: number
		data: LoanCalculatorFormValues
	}) => void
}

const theme = getTheme()

const LoanComparisonScenarioForm = ({
	loanId,
	loading = false,
	defaultValues,
	loanLocked = false,
	onSubmit,
	onCopyLoanToScenario,
}: LoanComparisonScenarioFormProps) => {
	const navigate = useNavigate()
	const { state } = useAppContext()
	const { siteConfig } = state as {
		siteConfig: SiteConfigurationByUrl | null
	}
	const ventiState = useVenti()
	const mixpanel = useMixpanel()
	const { formatMessage } = useIntl()
	const { alert } = useAlert()
	const [loadingMessage, setLoadingMessage] = useState('')

	const createLoanComparisonPdf = useMutation({
		mutationFn: () =>
			TheBigPOSApi.createLoanComparisonPdf(loanId, {
				siteConfigurationID: siteConfig?.id || '',
			}),
	})

	const getTaskDocumentsByLoan = useQuery({
		queryKey: [queryKeys.loanDocuments, loanId],
		queryFn: () => TheBigPOSApi.getTaskDocumentsByLoan(loanId),
		enabled: !!loanId && loanId !== '',
	})

	const methods = useForm<LoanCalculatorFormValues>({
		schema: loanCalculatorFormSchema,
		defaultValues: loanCalculatorFormDefaultValues || defaultValues,
		values: defaultValues,
	})

	const {
		handleSubmit,
		getValues,
		trigger,
		formState: { errors },
	} = methods

	const handleUpdateLoanClick = (data: LoanCalculatorFormValues) => {
		onSubmit?.(data)
	}

	const copyToScenario = async (index: number) => {
		const isValid = await trigger()
		if (!isValid) {
			return
		}

		const data = getValues()

		if (onCopyLoanToScenario) {
			onCopyLoanToScenario({ index, data })
		}
	}

	const handleCopyToScenario2 = async () => {
		await copyToScenario(0)
	}

	const handleCopyToScenario3 = async () => {
		await copyToScenario(1)
	}

	const handleGeneratePdf = async () => {
		try {
			setLoadingMessage(
				'Please be patient while the loan comparison document is generated'
			)
			await createLoanComparisonPdf.mutateAsync()
			setLoadingMessage('Fetching loan documents')
			const { data: loanDocuments } =
				await getTaskDocumentsByLoan.refetch()

			await ventiState.set(
				theme.storageKeys.loanDocs,
				loanDocuments?.data
			)

			mixpanel.trackEvent(eventTypes.GENERATE_LOAN_COMPARISON_PDF, {
				loanId,
			})

			const navigatePath = generatePath(
				`/${navigationLinking.Documents}`,
				{ loanId }
			)
			navigate(navigatePath)
		} catch {
			alert(formatMessage({ id: 'errors.general' }), {
				severity: 'error',
			})
		}
	}

	const formErrors = useMemo(
		() => formStateErrorsToErrorList(errors),
		[errors]
	)

	return (
		<>
			<FormProvider {...methods}>
				<LayoutRow className="grid grid-cols-1 gap-3 mb-6">
					<LoadingBtn
						text={
							loanLocked
								? `Loan is in use`
								: `Update Loan & Calculate Payment`
						}
						onClick={handleSubmit(handleUpdateLoanClick)}
						loading={loading || createLoanComparisonPdf.isPending}
						disabled={
							loading ||
							createLoanComparisonPdf.isPending ||
							loanLocked
						}
						variant="contained"
						fullWidth
					/>
					<ButtonGroup
						variant="contained"
						aria-label="Copy Scenarios"
						fullWidth
					>
						<Button
							text="Save to S2"
							onClick={handleCopyToScenario2}
							disabled={
								loading ||
								createLoanComparisonPdf.isPending ||
								getTaskDocumentsByLoan.isFetching
							}
						/>
						<Button
							text="Save to S3"
							onClick={handleCopyToScenario3}
							disabled={
								loading ||
								createLoanComparisonPdf.isPending ||
								getTaskDocumentsByLoan.isFetching
							}
						/>
					</ButtonGroup>
					<Button
						text="Generate Loan Comparison PDF"
						onClick={handleGeneratePdf}
						disabled={
							loading ||
							createLoanComparisonPdf.isPending ||
							getTaskDocumentsByLoan.isFetching
						}
						fullWidth
					/>
				</LayoutRow>
				<Form errors={formErrors}>
					<LayoutRow className="grid grid-cols-1 gap-3">
						<LoanCalculatorSharedFields loanLocked={loanLocked} />
					</LayoutRow>
					<LoanCalculatorResults loanCalculatorData={defaultValues} />
				</Form>
			</FormProvider>
			<ModalPleaseWait
				visible={createLoanComparisonPdf.isPending}
				message={loadingMessage}
			/>
		</>
	)
}

export default LoanComparisonScenarioForm
